import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead
} from "@mui/material";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import { getLabel, getCleanParameters } from "../MicroTip/DefaultParameters"

export default function CalibrationReport() {

    const { state } = useLocation();
    const navigate = useNavigate();

    const rawData = state?.runData;

    if (!rawData) return <Typography>No Report Data</Typography>;

    const description = rawData.description;

    const result = rawData.result || rawData?.resultData || {};

    const rows = rawData.rows || [];
    const parameters = getCleanParameters(rawData.parameters) || {};

    /* ---------------- PDF EXPORT ---------------- */
    const exportPDF = () => {
        const doc = new jsPDF();

        doc.text("LIMS Calibration Report", 14, 10);

        doc.text(`Description: ${description || "-"}`, 14, 26);

        let y = 35;

        /* RESULTS */
        autoTable(doc, {
            startY: y,
            head: [["Key", "Value"]],
            body: Object.entries(result).map(([k, v]) => [k, String(v)]),
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        y = doc.lastAutoTable.finalY + 10;

        /* CALIBRATION ROWS */
        autoTable(doc, {
            startY: y,
            head: [["Concentration", "Response"]],
            body: rows.map(r => [r.conc, r.resp]),
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        y = doc.lastAutoTable.finalY + 10;

        /* PARAMETERS */
        autoTable(doc, {
            startY: y,
            head: [["Parameter", "Value"]],
            body: Object.entries(parameters).map(([k, v]) => [
                getLabel(k),
                Array.isArray(v) ? v.join(", ") : String(v)
            ]),
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        doc.save(`calibration_${description}.pdf`);
    };

    /* ---------------- EXCEL EXPORT ---------------- */
    const exportExcel = () => {

        const wsData = [
            ["LIMS Calibration Report"],
            [],

            ["Description", description],
            [],
            ["RESULTS"],
            ["Key", "Value"],
            ...Object.entries(result).map(([k, v]) => [k, v]),
            [],
            ["CALIBRATION DATA"],
            ["Concentration", "Response"],
            ...rows.map(r => [r.conc, r.resp]),
            [],
            ["PARAMETERS"],
            ["Key", "Value"],
            ...Object.entries(parameters).map(([k, v]) => [
                getLabel(k),
                Array.isArray(v) ? v.join(", ") : v
            ])
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        const range = XLSX.utils.decode_range(ws["!ref"]);

        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });

            if (!ws[cellAddress]) continue;

            ws[cellAddress].s = {
                fill: {
                    fgColor: { rgb: "#774DEF" } 
                },
                font: {
                    bold: true,
                    color: { rgb: "FFFFFF" }
                },
                alignment: {
                    horizontal: "center"
                }
            };
        }

        ws["!cols"] = [
            { wch: 25 }, // Column A (min width for labels)
            { wch: 20 }, // Column B
            { wch: 20 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        const excelBuffer = XLSX.write(wb, {
            bookType: "xlsx",
            type: "array"
        });

        const file = new Blob([excelBuffer], {
            type: "application/octet-stream"
        });

        saveAs(file, `calibration_${description}.xlsx`);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1100, margin: "auto" }}>

            {/* ACTIONS */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2
                }}
            >

                {/* ================= LEFT: BACK BUTTON ================= */}
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/microtip")}
                >
                    Back
                </Button>

                {/* ================= RIGHT: EXPORT BUTTONS ================= */}
                <Box sx={{ display: "flex", gap: 2 }}>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={exportPDF}
                    >
                        Export PDF
                    </Button>

                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<GridOnIcon />}
                        onClick={exportExcel}
                    >
                        Export Excel
                    </Button>

                </Box>

            </Box>

            <Typography variant="h4" gutterBottom>
                LIMS Calibration Report
            </Typography>

            {/* DESCRIPTION */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography>
                    <b>Description:</b> {description || "-"}
                </Typography>
            </Paper>

            {/* RESULTS */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">Results</Typography>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><b>Key</b></TableCell>
                            <TableCell><b>Value</b></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {Object.entries(result).map(([k, v]) => (
                            <TableRow key={k}>
                                <TableCell>{k}</TableCell>
                                <TableCell>{String(v)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* CALIBRATION */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">Calibration Data</Typography>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><b>Concentration</b></TableCell>
                            <TableCell><b>Response</b></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((r, i) => (
                            <TableRow key={i}>
                                <TableCell>{r.conc}</TableCell>
                                <TableCell>{r.resp}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* PARAMETERS */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6">Parameters</Typography>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><b>Parameter</b></TableCell>
                            <TableCell><b>Value</b></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {Object.entries(parameters).map(([k, v]) => (
                            <TableRow key={k}>
                                <TableCell>{getLabel(k)}</TableCell>
                                <TableCell>
                                    {Array.isArray(v) ? v.join(", ") : String(v)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

        </Box>
    );
}